import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, Loader2, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { PREMIUM_TOURS } from "../../data/premiumTours";

const STATIC_PAGES = [
    { title: "Wellness Retreats", keywords: "yoga ayurveda meditation retreat wellness detox panchakarma silent", path: "/retreats", subtitle: "Yoga, Ayurveda & silent meditation" },
    { title: "Honeymoons", keywords: "honeymoon couple romantic wedding", path: "/honeymoons", subtitle: "Bespoke honeymoon journeys" },
    { title: "Premium India Tours", keywords: "tour luxury usd international private", path: "/tours", subtitle: "USD & EUR priced private journeys" },
    { title: "Sustainability & Impact", keywords: "sustainability conservation eco impact community tree", path: "/impact", subtitle: "Where your journey gives back" },
    { title: "MyEcoTour Rewards", keywords: "loyalty rewards points tier discount", path: "/loyalty", subtitle: "Customer loyalty programme" },
];

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function monthMatches(q) {
    const s = q.toLowerCase();
    return MONTH_NAMES.filter((m) => m.toLowerCase().startsWith(s)).flatMap((m) => {
        const n = MONTH_NAMES.indexOf(m) + 1;
        return [
            { type: "Travel month", title: `Where to go in ${m}`, subtitle: "The concierge month guide", path: `/where-to-go/${m.toLowerCase()}`, image: "" },
            { type: "Travel month", title: `${m} escapes`, subtitle: `Journeys best travelled in ${m}`, path: `/experiences?month=${n}`, image: "" },
        ];
    });
}

function tourMatches(q) {
    const s = q.toLowerCase();
    const statics = STATIC_PAGES.filter(
        (p) => p.title.toLowerCase().includes(s) || p.keywords.includes(s)
    ).map((p) => ({ type: "Page", title: p.title, subtitle: p.subtitle, path: p.path, image: "" }));
    return [
        ...PREMIUM_TOURS.filter(
            (t) => t.name.toLowerCase().includes(s) || t.strap.toLowerCase().includes(s) ||
                   t.regions.some((r) => r.toLowerCase().includes(s))
        ).map((t) => ({
            type: "Premium Tour", title: t.name, subtitle: t.strap,
            path: `/tours/${t.slug}`, image: t.heroImage,
        })),
        ...statics,
    ];
}

export const SearchDialog = ({ open, onClose }) => {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const timer = useRef(null);

    useEffect(() => {
        if (open) {
            setQ(""); setResults([]);
            setTimeout(() => inputRef.current?.focus(), 60);
        }
    }, [open]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        if (open) window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const onChange = (e) => {
        const value = e.target.value;
        setQ(value);
        clearTimeout(timer.current);
        if (value.trim().length < 2) { setResults([]); setLoading(false); return; }
        setLoading(true);
        timer.current = setTimeout(async () => {
            try {
                const { data } = await api.get("/search", { params: { q: value.trim() } });
                setResults([...monthMatches(value.trim()), ...tourMatches(value.trim()), ...(data.results || [])]);
            } catch { setResults([...monthMatches(value.trim()), ...tourMatches(value.trim())]); }
            setLoading(false);
        }, 300);
    };

    if (!open) return null;

    const groups = results.reduce((acc, r) => ((acc[r.type] = acc[r.type] || []).push(r), acc), {});

    return (
        <div className="fixed inset-0 z-[90] flex items-start justify-center pt-[12vh] px-4" data-testid="search-dialog">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
            <div className="relative w-full max-w-xl bg-background border border-border shadow-2xl animate-in fade-in slide-in-from-top-4 duration-300">
                <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
                    <Search size={17} strokeWidth={1.5} className="text-muted-foreground shrink-0" />
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={onChange}
                        placeholder="Search destinations, experiences, months, tours…"
                        data-testid="search-input"
                        className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-muted-foreground"
                    />
                    {loading && <Loader2 size={15} className="animate-spin text-muted-foreground" />}
                    <button type="button" onClick={onClose} aria-label="Close search" data-testid="search-close" className="p-1 text-muted-foreground hover:text-foreground">
                        <X size={17} strokeWidth={1.5} />
                    </button>
                </div>
                <div className="max-h-[55vh] overflow-y-auto">
                    {q.trim().length >= 2 && !loading && results.length === 0 && (
                        <p className="p-6 text-sm text-muted-foreground" data-testid="search-empty">
                            Nothing found for “{q}”. Try a destination, activity or festival — or ask our concierge.
                        </p>
                    )}
                    {Object.entries(groups).map(([type, items]) => (
                        <div key={type}>
                            <p className="px-5 pt-4 pb-1 text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-semibold">{type}s</p>
                            {items.map((r) => (
                                <Link
                                    key={r.path}
                                    to={r.path}
                                    onClick={onClose}
                                    data-testid={`search-result-${r.path.replaceAll("/", "-").slice(1)}`}
                                    className="flex items-center gap-4 px-5 py-3 hover:bg-secondary transition-colors group"
                                >
                                    {r.image ? (
                                        <img src={r.image} alt="" loading="lazy" className="w-12 h-12 object-cover shrink-0" />
                                    ) : (
                                        <span className="w-12 h-12 bg-secondary shrink-0" />
                                    )}
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-sm font-medium text-foreground truncate">{r.title}</span>
                                        {r.subtitle && <span className="block text-xs text-muted-foreground truncate">{r.subtitle}</span>}
                                    </span>
                                    <ArrowRight size={14} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                </Link>
                            ))}
                        </div>
                    ))}
                    {q.trim().length < 2 && (
                        <p className="p-6 text-xs text-muted-foreground">
                            Type at least two letters — try “tiger”, “aurora”, “yoga”, “Rajasthan”…
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};
